import { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { motion } from 'motion/react'
import { CheckCircle2, Gift, LayoutDashboard, Loader2, AlertCircle } from 'lucide-react'
import { supabase } from '@/integrations/supabase/client'

export default function EmailConfirmedPage() {
  const navigate = useNavigate()
  const [status, setStatus] = useState<'checking' | 'ok' | 'error'>('checking')
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  useEffect(() => {
    // Il link di conferma arriva con i token nell'hash: supabase-js li elabora
    // in modo asincrono (detectSessionInUrl), quindi restiamo in ascolto.
    const hash = window.location.hash.replace(/^#/, '')
    const params = new URLSearchParams(hash)
    const hashError = params.get('error_description') || params.get('error')

    if (hashError) {
      setErrorMessage(decodeURIComponent(hashError.replace(/\+/g, ' ')))
      setStatus('error')
      return
    }

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      if (session) setStatus('ok')
    })

    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session) {
        setStatus('ok')
      } else {
        // Nessun token nell'URL e nessuna sessione: link già usato o scaduto.
        setTimeout(() => {
          supabase.auth.getSession().then(({ data: { session: retry } }) => {
            if (retry) setStatus('ok')
            else setStatus((s) => (s === 'checking' ? 'error' : s))
          })
        }, 1500)
      }
    })

    return () => subscription.unsubscribe()
  }, [])

  return (
    <div className="min-h-screen relative flex items-center justify-center px-4 py-12">
      <div
        className="absolute inset-0 bg-cover bg-center bg-fixed"
        style={{ backgroundImage: 'url(/images/colorful-bg.webp)' }}
      />
      <motion.div
        className="relative w-full max-w-md glass rounded-lg p-8 sm:p-10 text-center overflow-hidden"
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <Link to="/" className="flex items-center justify-center gap-2 font-display text-lg">
          <span className="w-9 h-9 rounded-lg bg-neutral-900 text-white flex items-center justify-center">
            <Gift className="w-5 h-5" />
          </span>
          Make me a Gift
        </Link>

        {status === 'checking' && (
          <div className="mt-10 flex flex-col items-center gap-3 text-muted-foreground">
            <Loader2 className="w-7 h-7 animate-spin" />
            <p className="text-sm">Stiamo verificando la tua email…</p>
          </div>
        )}

        {status === 'ok' && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.35 }}
          >
            <div className="mx-auto mt-8 w-16 h-16 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center">
              <CheckCircle2 className="w-9 h-9" />
            </div>
            <h1 className="mt-6 text-2xl font-extrabold tracking-tight">
              Complimenti, hai confermato la tua email!
            </h1>
            <p className="mt-3 text-sm text-muted-foreground">
              Il tuo account è attivo: puoi iniziare subito a creare la tua lista dei desideri.
            </p>
            <button
              type="button"
              onClick={() => navigate('/dashboard')}
              className="mt-8 w-full text-sm font-medium text-white bg-neutral-900 rounded-full px-5 py-3 hover:bg-neutral-800 transition-colors flex items-center justify-center gap-2"
            >
              <LayoutDashboard className="w-5 h-5" />
              Vai alla Dashboard
            </button>
          </motion.div>
        )}

        {status === 'error' && (
          <div className="mt-8">
            <div className="mx-auto w-16 h-16 rounded-full bg-amber-100 text-amber-600 flex items-center justify-center">
              <AlertCircle className="w-9 h-9" />
            </div>
            <h1 className="mt-6 text-2xl font-extrabold tracking-tight">
              Non siamo riusciti a confermare l'email
            </h1>
            <p className="mt-3 text-sm text-muted-foreground">
              {errorMessage || 'Il link potrebbe essere scaduto o già utilizzato. Prova ad accedere: se il tuo account è già attivo entrerai senza problemi.'}
            </p>
            <Link
              to="/login"
              className="mt-8 inline-flex w-full items-center justify-center gap-2 text-sm font-medium text-white bg-neutral-900 rounded-full px-5 py-3 hover:bg-neutral-800 transition-colors"
            >
              Vai al login
            </Link>
          </div>
        )}
      </motion.div>
    </div>
  )
}
